// frontend/src/pages/builder/tableColumns.js
// Phase 3 step 7d-2 (decision D11): the Table visual's single Columns slot.
// Pure and unit-tested; no React.
//
// The draft stays { dimensions, measures } so every chart rule keeps working.
// The column ORDER is a separate list of keys, reconciled against the draft
// on every render: keys for items no longer in the draft fall out, new items
// append at the end. Dragging only ever changes that list.
//
// Every column is either a grouping ("Don't summarize") or a value with an
// aggregation, the way Power BI's table works. Numbers default to Sum;
// text, dates, yes/no and ids default to a grouping. Changing the picker
// converts the column in place. Limits mirror the server: 20 groupings and
// 10 values (MAX_DIMENSIONS and MAX_MEASURES in app/semantic/spec.py).
import {
  AGGREGATION_LABELS, MAX_MEASURES, MAX_TABLE_DIMENSIONS, aggregationsFor, dimensionItem, dimensionLabel, isMeasureField,
  measureLabel, sameMeasure, startingAggregation,
} from './builderSpec';
import { clampWidth } from '../../charts/tableOrder';

const NUMERIC = ['integer', 'number'];

export const GROUP = 'group';   // the picker value for "Don't summarize"

/** The key of a draft item: a grouping by field, a value by aggregation + field. */
export function columnKey(kind, item) {
  return kind === 'dimension' ? `d:${item.field}` : `m:${item.aggregation}:${item.field || ''}`;
}

/** Groupings then values: the order the server returns columns in. */
export function naturalOrder(draft) {
  return [
    ...draft.dimensions.map((d) => columnKey('dimension', d)),
    ...draft.measures.map((m) => columnKey('measure', m)),
  ];
}

/** The order list made to match the draft: kept keys in their order, then anything new. */
export function reconcileOrder(order, draft) {
  const natural = naturalOrder(draft);
  const present = new Set(natural);
  const kept = (order || []).filter((k, i, arr) => present.has(k) && arr.indexOf(k) === i);
  const seen = new Set(kept);
  return [...kept, ...natural.filter((k) => !seen.has(k))];
}

/** The columns as chips in display order, each knowing where it lives in the draft. */
export function orderedChips(draft, order, fieldsByKey = {}) {
  const byKey = new Map();
  draft.dimensions.forEach((d, index) => {
    byKey.set(columnKey('dimension', d), { kind: 'dimension', index, item: d, label: dimensionLabel(d, fieldsByKey) });
  });
  draft.measures.forEach((m, index) => {
    byKey.set(columnKey('measure', m), { kind: 'measure', index, item: m, label: measureLabel(m, fieldsByKey) });
  });
  return reconcileOrder(order, draft).map((key) => ({ key, ...byKey.get(key) }));
}

/** Put `key` before `beforeKey` (null = at the end). Unknown keys leave the order alone. */
export function moveKey(order, key, beforeKey = null) {
  if (!order.includes(key) || key === beforeKey) return order;
  const rest = order.filter((k) => k !== key);
  if (beforeKey == null || !rest.includes(beforeKey)) return [...rest, key];
  const at = rest.indexOf(beforeKey);
  return [...rest.slice(0, at), key, ...rest.slice(at)];
}

/** Insert a key that is not yet in the order before `beforeKey` (null = at the end). */
export function insertKey(order, key, beforeKey = null) {
  const rest = order.filter((k) => k !== key);
  return moveKey([...rest, key], key, beforeKey);
}

/** A key's item under a new aggregation keeps its place. */
export function replaceKey(order, oldKey, newKey) {
  return order.map((k) => (k === oldKey ? newKey : k));
}

/**
 * The draft's own lists put in display order (groupings among groupings,
 * values among values), so the first grouping column is the X axis if the
 * user switches to a chart, and the automatic sort follows the first column.
 */
export function alignDraft(draft, order) {
  const keys = reconcileOrder(order, draft);
  const rank = new Map(keys.map((k, i) => [k, i]));
  const by = (kind) => (a, b) => rank.get(columnKey(kind, a)) - rank.get(columnKey(kind, b));
  return {
    ...draft,
    dimensions: [...draft.dimensions].sort(by('dimension')),
    measures: [...draft.measures].sort(by('measure')),
  };
}

/**
 * presentation.columns for the server: the chart's items in display order,
 * each with the width the user dragged (7d-3, `widths` is { key: px }), or
 * null when the order is the natural one and no width is set (nothing
 * worth storing). Widths for columns no longer in the table are ignored.
 */
export function columnsPresentation(draft, order, widths = {}) {
  const keys = reconcileOrder(order, draft);
  const natural = keys.length < 2 || keys.every((k, i) => k === naturalOrder(draft)[i]);
  const width = (k) => clampWidth(widths && widths[k]);
  if (natural && !keys.some((k) => width(k) != null)) return null;
  const dims = new Map(draft.dimensions.map((d) => [columnKey('dimension', d), { field: d.field }]));
  const ms = new Map(draft.measures.map((m) => [
    columnKey('measure', m), m.field ? { aggregation: m.aggregation, field: m.field } : { aggregation: m.aggregation },
  ]));
  return keys.map((k) => {
    const w = width(k);
    return w == null ? dims.get(k) || ms.get(k) : { ...(dims.get(k) || ms.get(k)), width: w };
  });
}

/** The picker's options for a field: Don't summarize, then the aggregations the compiler accepts. */
export function useOptions(field) {
  if (isMeasureField(field)) return aggregationsFor(field).map((a) => [a, AGGREGATION_LABELS[a]]);   // never a grouping (13c-3)
  return [[GROUP, "Don't summarize"], ...aggregationsFor(field).map((a) => [a, AGGREGATION_LABELS[a]])];
}

/** What dropping a field on the table makes: numbers their default aggregation (Sum unless the Model view set another), everything else a grouping. */
export function tableUse(field) {
  if (isMeasureField(field)) return { kind: 'measure', item: { aggregation: 'metric', field: field.key } };
  if (field.role === 'measure' && NUMERIC.includes(field.data_type)) {
    return { kind: 'measure', item: { aggregation: startingAggregation(field), field: field.key } };
  }
  return { kind: 'dimension', item: dimensionItem(field) };
}

const TOO_MANY_GROUPINGS = `A table can have up to ${MAX_TABLE_DIMENSIONS} grouping columns.`;
const TOO_MANY_VALUES = `A table can show up to ${MAX_MEASURES} values.`;

/** Put an item in the draft, at `at` in its list or at the end. */
function place(draft, use, at = null) {
  const insert = (list, item) => (at == null ? [...list, item] : [...list.slice(0, at), item, ...list.slice(at)]);
  if (use.kind === 'dimension') {
    if (draft.dimensions.some((d) => d.field === use.item.field)) return { notice: 'already' };
    if (draft.dimensions.length >= MAX_TABLE_DIMENSIONS) return { notice: TOO_MANY_GROUPINGS };
    return { draft: { ...draft, dimensions: insert(draft.dimensions, use.item) } };
  }
  if (draft.measures.some((m) => sameMeasure(m, use.item))) return { notice: 'already' };
  if (draft.measures.length >= MAX_MEASURES) return { notice: TOO_MANY_VALUES };
  return { draft: { ...draft, measures: insert(draft.measures, use.item) } };
}

/**
 * Add a field as a column. Returns { draft, key, notice }: on a refusal the
 * draft is unchanged, key is null and the notice says why.
 */
export function addTableColumn(draft, field) {
  const use = tableUse(field);
  const r = place(draft, use);
  if (r.notice) {
    return { draft, key: null, notice: r.notice === 'already' ? `${field.display_name} is already in the table.` : r.notice };
  }
  return { draft: r.draft, key: columnKey(use.kind, use.item), notice: null };
}

/**
 * Change a column's picker: GROUP makes it a grouping, an aggregation makes
 * it a value with that aggregation. The column keeps its place (the caller
 * swaps the key in the order). Returns { draft, key, notice }.
 */
export function setColumnUse(draft, chip, field, use) {
  if (!chip || !field) return { draft, key: chip ? chip.key : null, notice: null };
  const options = useOptions(field).map(([v]) => v);
  if (!options.includes(use)) return { draft, key: chip.key, notice: null };
  const wantsGroup = use === GROUP;
  if (wantsGroup && chip.kind === 'dimension') return { draft, key: chip.key, notice: null };

  const without = chip.kind === 'dimension'
    ? { ...draft, dimensions: draft.dimensions.filter((_, i) => i !== chip.index) }
    : { ...draft, measures: draft.measures.filter((_, i) => i !== chip.index) };
  const next = wantsGroup
    ? { kind: 'dimension', item: dimensionItem(field) }
    : { kind: 'measure', item: { aggregation: use, field: field.key } };
  // A value changing its aggregation keeps its place in the draft too.
  const r = place(without, next, next.kind === chip.kind ? chip.index : null);
  if (r.notice) {
    return { draft, key: chip.key, notice: r.notice === 'already' ? 'That column is already in the table.' : r.notice };
  }
  return { draft: r.draft, key: columnKey(next.kind, next.item), notice: null };
}
